import {
  countriesWithCallingCode,
  defaultCountry,
  defaultCountryCode,
  fixIncorrectlySerializedPhoneNumber,
  lookupCountry,
  parsePhoneNumber,
  PhoneNumberValidationError,
  validatePhoneNumber,
} from "@/utils/phoneNumbers";
import { observer } from "mobx-react";
import { TMessage } from "@/i18n/tt";
import { makeAutoObservable } from "mobx";
import { getCountryName } from "@/utils/countries";
import { useEffect, useRef } from "react";
import { isBlank } from "@/utils/strings";
import { TextInput } from "./TextInput";
import { Language } from "@/i18n/languages";
import { when } from "@/utils/functions";
import { useLanguage, useTranslation } from "@/stores/RootStoreContext";
import { PhoneNumber } from "@/common/common";
import { Select } from "./Select";
import { sortBy } from "lodash";

type CountryOption = {
  isoCode: string;
  callingCode: string;
  name: string;
};

export class PhoneInputModel {
  locale: string;
  selectedCountry?: CountryOption;
  number: string;
  isRequired: boolean;
  error?: TMessage;
  changed: boolean = false;

  constructor({
    value,
    required,
    locale,
  }: { value?: PhoneNumber; required?: boolean; locale?: Language } = {}) {
    makeAutoObservable(this, {}, { autoBind: true });
    const phoneNumber = fixIncorrectlySerializedPhoneNumber(value);
    this.isRequired = required ?? false;
    this.locale = locale ?? Language.NO;
    const country =
      lookupCountry(phoneNumber?.countryCode ?? defaultCountryCode, phoneNumber?.value ?? "") ??
      defaultCountry;
    this.selectedCountry = this.countryOptions.find((o) => o.isoCode === country);
    this.number = phoneNumber?.value ?? "";
    // TODO format as you type
  }

  get countryOptions(): CountryOption[] {
    if (!this.locale) {
      return [];
    }
    return sortBy(
      countriesWithCallingCode.map((c) => ({
        isoCode: c.isoCode,
        callingCode: c.callingCode,
        name: getCountryName(c.isoCode, this.locale) ?? "",
      })),
      "name",
    );
  }

  get isValid(): boolean {
    return !this.error;
  }

  get isEmpty(): boolean {
    return isBlank(this.number);
  }

  get isChanged(): boolean {
    return this.changed;
  }

  setRequired(value: boolean) {
    this.isRequired = value;
  }

  setNumber(value: string) {
    this.number = value;
    this.changed = true;
  }

  setSelectedCountry(option: CountryOption) {
    this.selectedCountry = option;
    this.changed = true;
    this.validate();
  }

  getShortCountryLabel(item: CountryOption) {
    return `+${item.callingCode}`;
  }

  getCountryLabel(item: CountryOption): string {
    return `${getCountryName(item.isoCode, this.locale) ?? ""} (+${item.callingCode})`;
  }

  getCountryId(item: CountryOption): string {
    return item.isoCode;
  }

  setLocale(locale: string) {
    this.locale = locale;
  }

  commit() {
    if (this.number.startsWith("+")) {
      const n = parsePhoneNumber(this.number);
      if (n) {
        this.number = n.value;
        this.selectedCountry = this.countryOptions.find((o) => o.callingCode === n.countryCode);
      }
    }
    this.number = this.number.replace(/\D/g, "");
    this.validate();
  }

  validate(): boolean {
    if (this.isEmpty) {
      this.error = this.isRequired ? { key: "required" } : undefined;
      return this.isValid;
    }
    if (!this.selectedCountry) {
      this.error = { key: "phone-number.invalid-country-code" };
      return this.isValid;
    }
    switch (validatePhoneNumber(this.selectedCountry.callingCode, this.number)) {
      case PhoneNumberValidationError.INVALID_COUNTRY_CODE:
        this.error = { key: "phone-number.invalid-country-code" };
        break;
      case PhoneNumberValidationError.INVALID_NUMBER:
        this.error = { key: "phone-number.invalid-number" };
        break;
      default:
        this.error = undefined;
        break;
    }
    return this.isValid;
  }

  toValidPhoneNumber(): PhoneNumber | undefined {
    this.validate();
    if (this.isValid && !this.isEmpty) {
      return { value: this.number, countryCode: this.selectedCountry!.callingCode };
    }
  }
}

export const PhoneInput = observer(function PhoneInput({
  model,
  name,
  autoFocus,
  disabled,
  hideLabel,
}: {
  model: PhoneInputModel;
  name?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
}) {
  const { t } = useTranslation();
  const locale = useLanguage();
  const numberInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    model.setLocale(locale);
    if (autoFocus) {
      numberInputRef.current?.focus();
    }
  }, [autoFocus, model, locale]);

  return (
    <TextInput
      inputRef={numberInputRef}
      inputProps={{ autoComplete: "tel-national" }}
      disabled={disabled}
      autofocus={autoFocus}
      name={name}
      label={hideLabel ? undefined : t("phone-number-field.number-label")}
      value={model.number}
      onChange={(e) => model.setNumber(e.target.value)}
      onBlur={model.commit}
      error={Boolean(model.error)}
      message={when(model.error, t)}
      addonPosition={"left"}
      autoComplete={"tel"}
      addon={
        <Select
          grow={false}
          border={false}
          bg={"alt1"}
          rounded={"left"}
          items={model.countryOptions}
          idFn={model.getCountryId}
          labelFn={model.getShortCountryLabel}
          itemLabelFn={model.getCountryLabel}
          onSelect={model.setSelectedCountry}
          value={model.selectedCountry}
          className="focus:ring-2"
          label={t("phone-number-field.country-code-label")}
          labelSrOnly
        />
      }
    />
  );
});
